/**
 * Mecánica de administración por ejercicio — traducción literal de las
 * instrucciones exactas del master (§2.1 y §3.1). Es una tabla de datos pura:
 * la máquina de estados (peff-player.ts) la lee para saber qué paso viene, y la
 * UI para saber qué pintar. Cambiar una regla clínica se hace AQUÍ, no en la UI.
 *
 *  F1  sílabas    → audio modelo automático + grafema en grande + auto-stop.
 *  F2  denominar  → foto real, "¿Qué es?", sin texto ni modelo; pista a los 5 s.
 *  F3  repetir    → misma foto + audio modelo (solo ítems del adaptativo).
 *  F4  oraciones  → SOLO audio + animación de escucha; el texto jamás al niño.
 *  PSF1 denominar → igual que F2 (máximo UNA ayuda por ítem).
 *  PSF2 diferida  → modelo → pregunta distractora → "Ahora dila tú" → graba.
 */
import type { CodigoEjercicio } from "../api/types.js";

export interface Mecanica {
  /** Reproduce el audio modelo al entrar al ítem (F1, F3, F4, PSF2). */
  modeloAuto: boolean;
  /** Muestra el grafema en grande (solo F1: es una sílaba, no una palabra). */
  grafema: boolean;
  /** Muestra la fotografía real del banco (F2, F3, PSF1). */
  foto: boolean;
  /** Denominación: pista semántica a los 5 s → withHint; sin respuesta → NO_EVOCA. */
  denominacion: boolean;
  /** Intercala UNA pregunta distractora entre el modelo y la respuesta (PSF2). */
  distractora: boolean;
  /** Solo audio + animación de escucha, nunca estímulo visible (F4). */
  soloAudio: boolean;
  /** Control revelar 👁️ con blur, para el adulto (F4). */
  peekAdulto: boolean;
  /** Cierra la grabación sola por detección de voz o timeout (F1). */
  autoStop: boolean;
}

export const MECANICA: Record<CodigoEjercicio, Mecanica> = {
  F1: {
    modeloAuto: true, grafema: true, foto: false, denominacion: false,
    distractora: false, soloAudio: false, peekAdulto: false, autoStop: true,
  },
  F2: {
    modeloAuto: false, grafema: false, foto: true, denominacion: true,
    distractora: false, soloAudio: false, peekAdulto: false, autoStop: false,
  },
  F3: {
    modeloAuto: true, grafema: false, foto: true, denominacion: false,
    distractora: false, soloAudio: false, peekAdulto: false, autoStop: false,
  },
  F4: {
    modeloAuto: true, grafema: false, foto: false, denominacion: false,
    distractora: false, soloAudio: true, peekAdulto: true, autoStop: false,
  },
  PSF1: {
    modeloAuto: false, grafema: false, foto: true, denominacion: true,
    distractora: false, soloAudio: false, peekAdulto: false, autoStop: false,
  },
  PSF2: {
    modeloAuto: true, grafema: false, foto: false, denominacion: false,
    distractora: true, soloAudio: false, peekAdulto: false, autoStop: false,
  },
};

/** Ventana sin respuesta antes de la pista semántica y antes de NO_EVOCA (§2.1). */
export const ESPERA_DENOMINACION_MS = 5_000;

/** Auto-stop de F1: detección de voz o 4 s (§2.1). */
export const AUTO_STOP_F1_MS = 4_000;

/**
 * Suspensión automática por negativa sostenida. El master §1 (regla 4) dice
 * «>3 ítems sin intento» y el backend rechaza la suspensión con racha ≤ 3, así
 * que el umbral efectivo es 4 ítems consecutivos sin intento.
 */
export const UMBRAL_SIN_INTENTO = 3;
